import React, { useEffect, useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';

import TagManager from 'react-gtm-module';
import Flag from 'react-world-flags';
import Modal from 'react-bootstrap/Modal';

import { useLazyGetSchoolQuery } from 'services/exploreApi';
import { newDetailUrl } from 'common/utils/conversions';
import { useAppDispatch, useAppSelector } from 'app/hooks';

import styles from './SchoolCard.module.scss';

import { setFromExplore } from 'features/explore/exploreSlice';
import { OverviewComponent } from '../common/components/Overview';
import { navigateTop } from 'common/utils';
import { MatchingSection } from '../common/components/MatchingSection';
import { TagsSection } from '../common/components/TagsSection';
import { StudySection } from '../common/components/StudySection';
import { ContactBanner } from '../common/components/ContactBanner';
import { JourneyBanner } from '../common/components/ApplySection';

import { DetailSections } from '../common/components/DetailSections';
import { SessionState } from 'features/session/sessionSlice';
import {
  UserState,
  setSchoolShortlisted,
  setLastStepDone,
  setFavorites,
} from 'features/user/userSlice';

import { useAddFavoriteMutation, useDeleteFavoriteMutation } from 'services/exploreApi';
import Loader from 'common/components/Loader/Loader';

interface CompProps {
  schoolId: number;
  showModal: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
}
const SchoolCardModal = ({ schoolId, showModal, setShowModal }: CompProps): React.ReactElement => {
  const { logged } = useAppSelector(({ session }: { session: SessionState }) => session);
  // const [
  //   getSchools,
  //   { data: schools, isLoading: programsLoading, isFetching: programsFetching },
  // ] = useLazyGetBackofficeSchoolProgramsQuery();
  const [getSchool, { data: school, isLoading, isFetching }] = useLazyGetSchoolQuery();
  const [addFavorite] = useAddFavoriteMutation();
  const [deleteFavorite] = useDeleteFavoriteMutation();
  const [isFavorite, setFavorite] = useState<boolean>(school?.data.favorite || false);
  const { favorites } = useAppSelector(({ user }: { user: UserState }) => user);
  const navigate = useNavigate();
  const isMobile = useMediaQuery({ maxWidth: 768 });

  const dispatch = useAppDispatch();

  const handleCloseModal = () => setShowModal(false);
  const handleShowModal = () => setShowModal(true);

  const sendGTMEvent = (eventName: string) => {
    TagManager.dataLayer({
      dataLayer: {
        event: 'ga_event',
        eventName: eventName,
        schoolId: school?.data.id,
        schoolName: school?.data.name,
      },
    });
  };

  const handleAddFavorite = () => {
    const values = { id: school?.data.id, kind: 'School' };
    if (logged && school?.data.id) {
      addFavorite(values).then(async (resp: any) => {
        setFavorite(true);
        if (!favorites?.includes(school.data.id || 0)) {
          dispatch(setFavorites([...(favorites || []), school.data.id]));
        }
        dispatch(setSchoolShortlisted(true));
        dispatch(setLastStepDone(5));
      });
      sendGTMEvent('school_shortlist');
    } else {
      setTimeout(() => {
        navigate('/signup');
      }, 100);
    }
    return;
  };
  const handleDeleteFavorite = () => {
    const values = { id: school?.data.id, kind: 'School' };
    deleteFavorite(values).then(async (resp: any) => {
      setFavorite(false);
      dispatch(setFavorites(favorites?.filter((id) => id !== school?.data.id)));
    });
    // sendGTMEvent('program_unshortlist'); TO IMPLEMENT WITH SCHOOL TYPE
    return;
  };

  const navigateTo = (route: string) => {
    navigateTop();
    dispatch(setFromExplore(false));
    navigate(route);
  };

  useEffect(() => {
    if (showModal) {
      getSchool({ id: schoolId });
    }
  }, [showModal]);

  return (
    <Modal
      show={showModal}
      onHide={handleCloseModal}
      size="xl"
      dialogClassName="animate__animated animate__fadeIn animate__fast"
      contentClassName="position-relative rounded-4 border-0 shadow-sm"
    >
      {isFetching ? (
        <Loader />
      ) : (
        <Modal.Body className="p-0">
          <article className={styles.uaDetail}>
            <header
              className="ua-detail__header position-relative background-cover rounded-top-3 overflow-hidden"
              style={{
                backgroundImage: `url(${school?.data.cover || school?.data.photos?.[0]})`,
                backgroundPosition: 'bottom',
              }}
            >
              <div className={styles.stickyButtonContainer}>
                <button
                  className={`btn btn-light ms-auto ua-school__close-logo ${styles.stickyButton}`}
                  onClick={handleCloseModal}
                >
                  <i className="fs-4 bi bi-x-lg" />
                </button>
              </div>
              <div className="ua-school__logo shadow-sm position-absolute bottom-0 left-0 mb-3 ms-5 p-2 d-flex align-items-center justify-content-center bg-white rounded-2">
                <img className="w-100" src={school?.data.logo} />
              </div>
            </header>
            <section className="pb-3 px-3 px-md-5 pt-2 mt-4  position-sticky top-0 bg-white z-1 border-bottom border-light-primary">
              <div className="d-flex flex-row flex-wrap justify-content-center justify-content-lg-start">
                <div className={styles.titleContainer}>
                  <h1 className="fw-semibold mb-0 text-center text-lg-start animate__animated animate__fadeIn">
                    {school?.data.name}
                  </h1>
                </div>
                <button
                  className="ua-school__card_favourites btn me-1 fs-4 text-secondary"
                  onClick={isFavorite ? () => handleDeleteFavorite() : () => handleAddFavorite()}
                >
                  <i className={isFavorite ? 'bi bi-heart-fill' : 'bi bi-heart'}></i>
                </button>
                {/* <button className="btn btn-lg ms-auto btn-primary rounded-pill fw-medium px-4 w-100 w-md-auto">
                  Talk with admissions
                </button> */}
              </div>
              <div className="d-flex align-items-center gap-2">
                <h5 className="mb-0">{school?.data.city.country.name}</h5>
                <Flag code={school?.data.city.country.code} className={styles.countryFlag} />
              </div>
              <div className={`d-flex flex-row gap-3 ua-detail__header-btns mt-5`}>
                <a
                  type="button"
                  className="ua-detail__explore-btn btn btn-primary btn-lg bg-light-blue br-50 text-center fw-600 fs-6 "
                  href={`https://unifyapply.com/experts/request-meeting?school_id=${school?.data.id}&school_name=${school?.data.name}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  Ask expert for help
                </a>
                <a
                  className="ua-detail__header-btns__detail-btn btn btn-outline-light-blue"
                  // onClick={() => {
                  //   navigateTo(newDetailUrl('schools', school?.data.id, school?.data.name));
                  // }}
                  href={newDetailUrl('schools', school?.data.id, school?.data.name)}
                  target="_blank"
                  rel="noreferrer"
                >
                  Read more
                </a>
              </div>
            </section>
            <main className="overflow-hidden rounded-bottom-3 animate__animated animate__fadeIn">
              <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                <OverviewComponent entity={school?.data} comesFromExplore={true} />
              </section>
              <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                <MatchingSection entity={school} comesFromExplore={true} />
              </section>
              {school?.data.labels && school?.data.labels.length > 0 && (
                <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                  <TagsSection entity={school?.data} comesFromExplore={true} />
                </section>
              )}

              <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                <StudySection
                  school={school}
                  schoolId={Number(schoolId)}
                  logoSchool={String(school?.data.logo)}
                  comesFromExplore={true}
                />
              </section>
              {!!school?.data.contact_banner && (
                <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                  <ContactBanner entity={school} />
                </section>
              )}
              <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                <DetailSections entity={school?.data} />
              </section>
              <section className="p-3 px-3 px-md-5 my-2 border-top border-light-primary border-top-0 pt-0 pb-0">
                <JourneyBanner logged={logged} navigateTo={navigateTo} isMobile={isMobile} />
              </section>
            </main>
          </article>
        </Modal.Body>
      )}
    </Modal>
  );
};

export default SchoolCardModal;
